import {createContext, ReactNode, useContext, useMemo} from 'react';
import {
  BodySite,
  BodySiteCode,
  BodySiteId,
  useBodySites,
  indexArrayBy,
  useScoringSystems,
  ScoringSystem,
  ScoringSystemId,
  Pathology,
  PathologyId
} from '@legit.health/ui';
import useAppInfo from './useAppInfo';
import usePathologies from './usePathologies';
import cachedDataState from './utils/cachedDataState';

export type SharedDataContextValue = {
  pathologies: Pathology[];
  pathologiesById: Record<PathologyId, Pathology>;
  bodySites: BodySite[];
  bodySitesById: Record<BodySiteId, BodySite>;
  bodySitesByCode: Record<BodySiteCode, BodySite>;
  scoringSystems: ScoringSystem[];
  scoringSystemsById: Record<ScoringSystemId, ScoringSystem>;
  clear: () => void;
};

const SharedDataContext = createContext<SharedDataContextValue>({
  pathologies: [],
  pathologiesById: {},
  bodySites: [],
  bodySitesById: {},
  bodySitesByCode: {},
  scoringSystems: [],
  scoringSystemsById: {},
  clear: () => {}
});

export function useSharedDataContext(): SharedDataContextValue {
  return useContext(SharedDataContext);
}

export function SharedDataContextProvider({children}: {children: ReactNode}) {
  useAppInfo();
  return <SharedDataLoader>{children}</SharedDataLoader>;
}

function SharedDataLoader({children}: {children: ReactNode}) {
  const {apiResponse: getBodySitesResponse} = useBodySites({
    onSuccess: function (response) {
      cachedDataState.set((c) => ({
        ...c,
        bodySites: response.data
      }));
    }
  });
  const {apiResponse: getPathologiesResponse} = usePathologies();
  const {apiResponse: getScoringSystemsResponse} = useScoringSystems({
    onSuccess: function (response) {
      cachedDataState.set((c) => ({
        ...c,
        scoringSystems: response.data
      }));
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const scoringSystems = getScoringSystemsResponse!.data;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const bodySites = getBodySitesResponse!.data;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const pathologies = getPathologiesResponse!.data;

  const value = useMemo(
    () => ({
      bodySites,
      bodySitesById: indexArrayBy(bodySites, 'id'),
      bodySitesByCode: indexArrayBy(bodySites, 'code'),
      pathologies,
      pathologiesById: indexArrayBy(pathologies, 'id'),
      scoringSystems,
      scoringSystemsById: indexArrayBy(scoringSystems, 'id'),
      clear: () => cachedDataState.delete()
    }),
    [bodySites, pathologies, scoringSystems]
  );

  return <SharedDataContext.Provider value={value}>{children}</SharedDataContext.Provider>;
}
